import { msg } from "@lit/localize";
import type { RunButton } from "@whilecat/core/buttons/run-button.ts";
import type { CodeArea } from "@whilecat/core/editors/code-area.ts";
import { defaultJavaEditorSettings } from "@whilecat/core/editors/code-editor-fabric.ts";
import {
    AlertType,
    renderErrorAlert,
    renderInfoAlert,
    renderRawAlert,
    renderSuccessAlert,
} from "@whilecat/core/tasks/alerts.ts";
import { BaseTask } from "@whilecat/core/tasks/base-task.ts";
import type { CodeVerifier } from "@whilecat/core/tasks/common/code-verifier.ts";
import type { CodeParams } from "@whilecat/core/tasks/common/params/code-task-params.ts";
import { VerificationError } from "@whilecat/core/tasks/common/verification-error.ts";
import { renderPlaceholderError } from "@whilecat/core/tasks/java/renderers/output-renderer.ts";
import { getUserId } from "@whilecat/core/utils/cookie-utils.ts";
import { isPresent } from "@whilecat/core/utils/objects.ts";
import { isNotBlank } from "@whilecat/core/utils/string.ts";
import WhilecatStyle from "@whilecat/core/whilecat-style.js";
import type { CodeType } from "@whilecat/services/java-compiler-service.ts";
import { MetricType, saveMetric } from "@whilecat/services/metric-service.js";
import { StatisticsEventType, collectEvent } from "@whilecat/services/statistics.js";
import { type TemplateResult, css, html, nothing } from "lit";
import { property, query } from "lit/decorators.js";

import "@whilecat/core/editors/code-area.ts";
import "@whilecat/core/buttons/run-button.ts";

export enum ResultType {
    SOLVED = "SOLVED",
    FAILED = "FAILED",
}

export type ExecutionResult<T> =
    | {
          output: T;
          result: "SUCCESS";
          payload?: string | undefined;
      }
    | {
          output: string;
          result: "ERROR" | "TIMEOUT";
          payload?: string | undefined;
      };

const defaultTransformer = <T extends {}>(output: string | T) => output.toString();

export abstract class BaseCodeTask<T, R extends {} = string> extends BaseTask<T> {
    static styles = [
        WhilecatStyle,
        super.styles,
        css`
          :host {
            width: 100%;
          }
          .input-block {
            display: flex;
            flex-direction: column;
            gap: 24px;
          }

          .output {
            font-family: monospace;
          }

          placeholder {
            color: var(--whilecat-pink-2)
          }

          #user-input-button {
            width: var(--whilecat-run-button-width);
          }

          code-area {
              width: 100%;
          }
        `,
    ];

    @property({ type: Boolean }) private error = false;
    @property({ attribute: false }) data!: T & CodeParams;
    @property({ type: Boolean }) private verificationInProgress = false;

    @query("#user-input") codeArea!: CodeArea;
    @query("#user-input-button") runButton!: RunButton;

    private specialOutput?: TemplateResult | undefined;
    private lastResult?: ExecutionResult<R> | undefined;
    private failCounter = 0;
    private attemptCounter = 0;
    abstract codeVerifier: CodeVerifier<R>;
    abstract codeType: CodeType;
    abstract executor(code: string, type: CodeType): Promise<ExecutionResult<R>>;

    transformer(output: R | string): string | TemplateResult {
        return defaultTransformer(output);
    }

    async inputKeyDown(evt: KeyboardEvent) {
        if (evt.key === "Enter" && evt.metaKey) {
            evt.stopPropagation();
            await this.verify();
        }
    }

    setInputEnabled(enabled: boolean, verificationInProgress: boolean) {
        this.verificationInProgress = verificationInProgress;

        if (enabled) {
            this.codeArea.setEditable(true);
            this.runButton.removeAttribute("disabled");
        } else {
            this.codeArea.setEditable(false);
            this.runButton.setAttribute("disabled", "true");
        }
    }

    getCode() {
        return this.codeArea.getCode();
    }

    getFullCode() {
        let resultCode = this.getCode();

        if (this.data.preCode != null) {
            resultCode = this.data.preCode + "\n\n" + resultCode;
        }

        if (this.data.postCode != null) {
            resultCode += "\n\n" + this.data.postCode;
        }
        return resultCode;
    }

    getLastResult() {
        return this.lastResult;
    }

    getRenderableOutput() {
        if (!this.lastResult) {
            return;
        }
        return this.transformer(this.lastResult.output);
    }

    async verify() {
        this.attemptCounter++;
        this.setInputEnabled(false, true);

        let resultCode = this.getFullCode();

        if (!this.codeVerifier.checkPlaceholdersReplaced(resultCode)) {
            setTimeout(() => this.reportSpecialCase(renderPlaceholderError()), 500);
            return;
        }

        const predefinedResult = this.codeVerifier.predefinedResult(this.getCode());
        if (isPresent(predefinedResult)) {
            setTimeout(() => this.reportCompleted(predefinedResult!), 1000);
            return;
        }

        this.lastResult = await this.executor(resultCode, this.codeType);

        try {
            this.codeVerifier.checkForErrors(this.lastResult);
            this.codeVerifier.checkResult(this.lastResult);
            this.codeVerifier.checkAnswer(this.getCode());

            // Correct
            this.reportCompleted(this.lastResult);
        } catch (error) {
            if (error instanceof VerificationError) {
                this.reportFailed(error.result);
            } else {
                console.error(error);
                this.reportFailed(this.createErrorResult(msg("Something went wrong. Please try again.")));
            }
        } finally {
            if (this.failCounter >= 3) {
                this.codeArea.enableShowAnswerButton();
            }
        }
    }

    protected reportSpecialCase(errorText: TemplateResult) {
        this.reportFailed();
        this.specialOutput = errorText;
    }

    protected reportFailed(result?: ExecutionResult<R>) {
        this.specialOutput = undefined;
        this.lastResult = result;
        this.error = true;
        this.failCounter++;
        this.setInputEnabled(true, false);

        this.reportMetric(ResultType.FAILED);

        collectEvent(StatisticsEventType.TASK_FAILED, {
            taskId: this.taskId,
            counter: this.failCounter,
        });
    }

    protected reportCompleted(result: ExecutionResult<R>) {
        this.lastResult = result;
        this.error = false;
        this.publishCompleted();
        // FIXME: here's the bug with enabling input for example tasks after first execution
        // it happens because one flag is treated for both: run button and the editor itself
        this.setInputEnabled(this.data.repeatableRun, false);
        this.reportMetric(ResultType.SOLVED);

        this.codeArea.blur();
    }

    private reportMetric(result: ResultType) {
        // we're interested in only in practice tasks
        if (!this.data.editable) {
            return;
        }

        saveMetric(
            MetricType.JAVA_TASK_SOLVE_ATTEMPT,
            JSON.stringify({
                userId: getUserId(),
                taskId: this.taskId,
                attempts: this.attemptCounter,
                failures: this.failCounter,
                result: result,
                code: this.getCode(),
            }),
        );
    }

    override showCorrectAnswer() {
        this.gaveUp = true;

        if (isNotBlank(this.data.correctCode)) {
            this.codeArea.setCode(this.data.correctCode);
        }
    }

    protected onResetCodeButton() {
        this.codeArea.setCode(this.data.code);
    }

    protected renderOutput() {
        // If the user completed the task, and wants to play around, don't render
        // the output as error.
        if (this.error && !this.completed) {
            return this.renderError();
        } else if (isPresent(this.getRenderableOutput())) {
            const practice = this.data.editable && isNotBlank(this.data.correctCode);

            return practice
                ? renderSuccessAlert(this.getRenderableOutput())
                : renderInfoAlert(this.getRenderableOutput());
        }
        return nothing;
    }

    private renderError() {
        if (isPresent(this.specialOutput)) {
            return renderRawAlert(AlertType.WARNING, "", this.specialOutput!);
        }
        return renderErrorAlert(this.getRenderableOutput());
    }

    get settings() {
        return defaultJavaEditorSettings(
            this.data.code,
            this.data.editable,
            false,
            this.data.showAnswerButtonEnabled,
            () => this.showCorrectAnswer(),
            () => this.onResetCodeButton(),
            this.data.readonlyRanges,
            this.data.foldRanges,
            this.data.tooltips,
        );
    }

    render() {
        return html`
          <div class="container">
            ${this.data.content}
            </br>
            <div class="input-block">
              <code-area id='user-input'
                         .settings="${this.settings}"
                         @keydown='${this.inputKeyDown}'
              ></code-area>

              <run-button id="user-input-button"
                          buttonText=${msg("Run")}
                          .onClickFunction="${() => this.verify()}"
                          .processing="${this.verificationInProgress}"
              ></run-button>
              ${this.renderOutput()}
            </div>
          </div>
        `;
    }

    createErrorResult(output: string): ExecutionResult<R> {
        return {
            output: output,
            result: "ERROR",
        };
    }

    // TEST ONLY METHOD
    _setLastResult(result: ExecutionResult<R>) {
        this.lastResult = result;
    }

    _getData() {
        return this.data;
    }

    _setData(data: any) {
        this.data = data;
    }
}
